import React, { useRef } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Box,
  Flex,
  Text,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react'
import { MdPrint, MdConfirmationNumber } from 'react-icons/md'
import { useGetTicketPreview } from 'queries/event.query'

export default function TicketPreviewModal({ isOpen, onClose, event }) {
  const iframeRef = useRef(null)
  const bg = useColorModeValue('white', 'navy.800')

  const { data, isLoading, isError, error } = useGetTicketPreview(event?.id, {
    enabled: isOpen && !!event?.id,
  })

  const previewHtml = data?.html?.replace(
    '</head>',
    `<style>@media screen { .ticket-page-item > .flex-1 > div:nth-child(2) { position: absolute !important; left: 20px !important; right: 20px !important; bottom: 8px !important; margin: 0 !important; } }</style></head>`,
  )

  const handlePrint = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.focus()
      iframeRef.current.contentWindow.print()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(5px)" />
      <ModalContent bg={bg} borderRadius="2xl" maxW="980px" overflow="hidden" boxShadow="2xl">
        <ModalHeader borderBottom="1px solid" borderColor="gray.100" py={4} px={6}>
          <Flex align="center" justify="space-between" pr={6}>
            <HStack spacing={3}>
              <Box p={2} bg="blue.50" color="brand.500" borderRadius="lg">
                <MdConfirmationNumber size={22} />
              </Box>
              <Box>
                <Text fontSize="md" fontWeight="bold">
                  Ticket Preview
                </Text>
                <Text fontSize="xs" color="gray.500" noOfLines={1} maxW="500px">
                  {event?.title || 'Event'}
                </Text>
              </Box>
            </HStack>
            <Badge colorScheme="purple" px={3} py={1} borderRadius="full" fontSize="xs">
              Live Production Template
            </Badge>
          </Flex>
        </ModalHeader>
        <ModalCloseButton top={4} right={4} />

        <ModalBody p={6} bg="gray.50">
          {isLoading && (
            <Flex justify="center" align="center" direction="column" minH="340px" gap={4}>
              <Spinner size="xl" thickness="4px" speed="0.65s" color="brand.500" />
              <Text fontSize="sm" color="gray.500" fontWeight="medium">
                Rendering pixel-perfect ticket preview...
              </Text>
            </Flex>
          )}

          {isError && (
            <Alert status="error" borderRadius="xl" my={4}>
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Failed to render preview</Text>
                <Text fontSize="xs">
                  {error?.response?.data?.message || error?.message || 'Server error occurred'}
                </Text>
              </Box>
            </Alert>
          )}

          {!isLoading && !isError && data?.html && (
            <Box
              w="100%"
              display="flex"
              justifyContent="center"
              alignItems="center"
              overflowX="auto"
              py={4}
            >
              <Box
                w="900px"
                h="315px"
                bg="white"
                borderRadius="xl"
                boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)"
                overflow="hidden"
                border="1px solid"
                borderColor="gray.200"
              >
                <iframe
                  ref={iframeRef}
                  title="Ticket Preview Frame"
                  srcDoc={previewHtml}
                  style={{
                    width: '900px',
                    height: '315px',
                    border: 'none',
                    display: 'block',
                  }}
                />
              </Box>
            </Box>
          )}

          <Box mt={3} p={3} bg="blue.50" borderRadius="xl" border="1px solid" borderColor="blue.100">
            <Text fontSize="xs" color="blue.700" textAlign="center">
              💡 <strong>Accurate Preview:</strong> This preview uses the authoritative production ticket engine (Puppeteer/Blade) with sample order and tier data. No fake order or database record is created.
            </Text>
          </Box>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.100" py={3} px={6}>
          <HStack spacing={3}>
            <Button
              leftIcon={<MdPrint />}
              colorScheme="teal"
              variant="outline"
              size="sm"
              onClick={handlePrint}
              isDisabled={!data?.html}
            >
              Print / Save PDF
            </Button>
            <Button colorScheme="gray" size="sm" onClick={onClose}>
              Close
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
